import thunkHandler from "../thunkHandler";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { BASEURI, headers } from "../../utils/helper";

// ─────────────────────────────────────────────
// GET DAY SLOTS
// GET /available/slots?specialistId=&date=
// (public route — no auth required)
// ─────────────────────────────────────────────
export const getAllAppointmentSlots = createAsyncThunk(
  "apt/day-slots",
  async (data, thunkAPI) => {
    const params = {
      date: data?.date || null,
      specialistId: data?.specialistId || null,
    };

    return thunkHandler(
      {
        params,
        method: "GET",
        headers: headers(),
        url: `${BASEURI}/available/slots`,
      },
      thunkAPI,
      { successToast: false }
    );
  }
);

// ─────────────────────────────────────────────
// GET MONTHLY SLOTS (calendar view)
// GET /available/slots/month?specialistId=&month=&year=
// (public route — no auth required)
// ─────────────────────────────────────────────
export const getAllMonthlySlots = createAsyncThunk(
  "apt/monthly-slots",
  async (data, thunkAPI) => {
    const params = {
      year: data?.year || null,
      month: data?.month || null,
      specialistId: data?.specialistId || null,
    };

    return thunkHandler(
      {
        params,
        method: "GET",
        headers: headers(),
        url: `${BASEURI}/available/slots/month`,
      },
      thunkAPI,
      { successToast: false }
    );
  }
);

// ─────────────────────────────────────────────
// LOCK SLOT (Step 1 of booking flow)
// POST /member/appointments/lock-slot
// Reserves slot for 10 minutes, returns lockId
//
// Payload: { specialistId, slotId, date, timezone }
//   slotId = slot._id from getAvailableSlots response (identity key)
//   date   = "YYYY-MM-DD" in member's local timezone
// ─────────────────────────────────────────────
export const lockSlot = createAsyncThunk(
  "apt/lock-slot",
  async (data, thunkAPI) => {
    return thunkHandler(
      {
        method: "POST",
        headers: headers(),
        url: `${BASEURI}/member/appointments/lock-slot`,
        data: {
          specialistId: data?.specialistId,
          slotId:       data?.slotId,       // identity key from availability response
          date:         data?.date,          // "YYYY-MM-DD" calendar date
          timezone:     data?.timezone,
        },
      },
      thunkAPI,
      { successToast: false }
    );
  }
);

// ─────────────────────────────────────────────
// RELEASE LOCK / CANCEL UNPAID CHECKOUT
// POST /member/appointments/release-lock
// ─────────────────────────────────────────────
export const releaseLock = createAsyncThunk(
  "apt/release-lock",
  async (data, thunkAPI) => {
    return thunkHandler(
      {
        method: "POST",
        headers: headers(),
        url: `${BASEURI}/member/appointments/release-lock`,
        data: {
          lockId: data?.lockId,
          specialistId: data?.specialistId,
          slotId: data?.slotId,
          date: data?.date,
          bookingId: data?.bookingId,
        },
      },
      thunkAPI,
      { successToast: false }
    );
  }
);

// ─────────────────────────────────────────────
// CONFIRM BOOKING (Step 2 of booking flow)
// POST /member/appointments/confirm-booking
// Converts lock → BookingAppointment
// ─────────────────────────────────────────────
export const confirmBooking = createAsyncThunk(
  "apt/confirm-booking",
  async (data, thunkAPI) => {
    return thunkHandler(
      {
        method: "POST",
        headers: headers(),
        url: `${BASEURI}/member/appointments/confirm-booking`,
        data: {
          specialistId: data?.specialistId,
          lockId: data?.lockId,
          notes: data?.notes || "",
          timezone: data?.timezone,
          termsAccepted: data?.termsAccepted ?? true,       // required by backend
          isFormFlowBooking: data?.isFormFlowBooking ?? false,
          formData: data?.formData ?? null,
          bookingSource: data?.bookingSource ?? "web",
        },
      },
      thunkAPI,
      { successToast: true, successMsg: "Appointment booked successfully!" }
    );
  }
);

// ─────────────────────────────────────────────
// INITIATE BOOKING (Step 2 of Stripe booking flow)
// POST /member/appointments/initiate-booking
// Converts lock → BookingAppointment & Payment → Redirects to Stripe
// ─────────────────────────────────────────────
export const initiateBookingWithPayment = createAsyncThunk(
  "apt/initiate-booking",
  async (data, thunkAPI) => {
    return thunkHandler(
      {
        method: "POST",
        headers: headers(),
        url: `${BASEURI}/member/appointments/initiate-booking`,
        data: {
          specialistId: data?.specialistId,
          lockId: data?.lockId,
          notes: data?.notes || "",
          timezone: data?.timezone,
          termsAccepted: data?.termsAccepted ?? true,
          isFormFlowBooking: data?.isFormFlowBooking ?? false,
          formData: data?.formData ?? null,
          bookingSource: data?.bookingSource ?? "web",
        },
      },
      thunkAPI,
      { successToast: false }
    );
  }
);